import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger, MatMenu } from "@angular/material/menu";
import { Router } from "@angular/router";
import { SolverService } from "@app/services/rest/solver.service";
import { SolverInfo, SolverIssue } from "@app/models/dto/solver-issue";
import { ResourceAvailability } from "@app/models/rest/resource-availability";
import { Ticket } from "@app/models/rest/ticket";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import moment from "moment";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { AvailabilityEditorComponent } from "@app/view/diary/availability-editor/availability-editor.component";
import { PriorityStore } from "@app/services/store/priority-store";
import { Priority } from "@app/models/rest/priority";
import { AlertData } from "@app/view/shared/alerts/alert-tool/alert.data";
import { DayProblems } from "@app/view/shared/alerts/alert-tool/day.problems";
import { AvailabilityIssue } from "@app/view/shared/alerts/alert-tool/availability.issue";
import { MatIconButton } from "@angular/material/button";
import { NgClass } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatChipSet, MatChip } from "@angular/material/chips";
import { MatTooltip } from "@angular/material/tooltip";
import { MomentModule } from "ngx-moment";
import { translateSolverRule } from "@app/core/utils/solver-rule-translations";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { UserService } from "@app/services/rest/user.service";
import { ScheduleProjectService } from "@app/services/project/schedule-project.service";

@Component({
  selector: "app-reservation-alert-tool",
  templateUrl: "./reservation-alert-tool.component.html",
  styleUrls: ["./reservation-alert-tool.component.scss"],
  imports: [
    MatIconButton,
    MatMenuTrigger,
    NgClass,
    MatIcon,
    MatMenu,
    MatChipSet,
    MatChip,
    MatTooltip,
    MomentModule
  ]
})
export class ReservationAlertToolComponent implements OnInit {
  solver = inject(SolverService);
  dialog = inject(MatDialog);
  router = inject(Router);
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  ticketStore = inject(TicketStreamingStore);
  priorityStore = inject(PriorityStore);
  clientConfigService = inject(ClientConfigService);
  userService = inject(UserService);
  scheduleProjectService = inject(ScheduleProjectService);

  @ViewChild("alertTrigger", { read: MatMenuTrigger, static: false })
  public alertTriggerBtn: MatMenuTrigger;
  public hasAlerts = false;
  alertData: AlertData;
  alertCount: number = 0;
  resourceAvailabilities: Map<number, ResourceAvailability> = new Map<
    number,
    ResourceAvailability
  >();
  tickets: Map<number, Ticket> = new Map<number, Ticket>();
  private priorityMap: Map<number, Priority> = new Map<number, Priority>();
  private lastSolverState: SolverInfo;
  private plannerTeamNames: Set<string> = new Set();

  private isAvailabilityInPlannerTeams(availabilityId: number): boolean {
    const cached = this.resourceAvailabilities.get(availabilityId);
    if (!cached) return true;
    const resourceId = cached.resource?.id;
    if (!resourceId) return true;
    const project = this.scheduleProjectService.projectData.getValue();
    const resource = project.resourceStore.getById(resourceId);
    if (!resource) return true;
    const teamName = (resource as any).get("team");
    return teamName && this.plannerTeamNames.has(teamName);
  }

  private filterAvailabilitiesByTeam(availabilityIds: number[]): number[] {
    if (
      !this.clientConfigService.config.restrictAlertsByPlannerTeams ||
      this.plannerTeamNames.size === 0
    ) {
      return availabilityIds;
    }
    return availabilityIds.filter((id) =>
      this.isAvailabilityInPlannerTeams(id)
    );
  }

  menuOpened() {
    this.updateAlerts(this.lastSolverState);
  }

  updateCounts(value: SolverInfo) {
    if (!value.issues) {
      console.log("no info");
      return;
    }

    this.alertCount = 0;
    let resourceAvailabilityAlerts = value.issues.filter(
      (issue) =>
        issue.resourceAvailabilities && issue.resourceAvailabilities.length > 0
    );

    resourceAvailabilityAlerts.forEach((availabilityAlert) => {
      const filteredAvailabilities = this.filterAvailabilitiesByTeam(
        availabilityAlert.resourceAvailabilities
      );
      filteredAvailabilities.forEach(() => {
        switch (availabilityAlert.rule) {
          case "Normal Priority Reservation Exceeded":
          case "High Priority Reservation Exceeded":
          case "Emergency Priority Reservation Exceeded":
            this.alertCount++;
            break;
          default:
            break;
        }
      });
    });
    this.hasAlerts = this.alertCount > 0;
  }

  updateAlerts(value: SolverInfo) {
    console.log("updating alerts with info ", value);
    if (!value.issues) {
      console.log("no info");
      return;
    }
    let alertData = new AlertData();
    alertData.dates = [];
    this.alertCount = 0;
    let resourceAvailabilityAlerts = value.issues.filter(
      (issue) =>
        issue.resourceAvailabilities && issue.resourceAvailabilities.length > 0
    );

    resourceAvailabilityAlerts.forEach((availabilityAlert) => {
      const filteredAvailabilities = this.filterAvailabilitiesByTeam(
        availabilityAlert.resourceAvailabilities
      );
      filteredAvailabilities.forEach((value1) => {
        switch (availabilityAlert.rule) {
          case "Normal Priority Reservation Exceeded":
          case "High Priority Reservation Exceeded":
          case "Emergency Priority Reservation Exceeded":
            if (this.resourceAvailabilities.has(value1)) {
              this.alertCount++;
              this.updateDayProblemAvailabilities(
                alertData,
                availabilityAlert,
                this.resourceAvailabilities.get(value1)
              );
            } else {
              this.resourceAvailabilityStore
                .loadSingleProjection<ResourceAvailability>(
                  ResourceAvailability,
                  value1,
                  "detail"
                )
                .then((value2) => {
                  this.resourceAvailabilities.set(value1, value2);
                  if (this.isAvailabilityInPlannerTeams(value1)) {
                    this.alertCount++;
                    this.updateDayProblemAvailabilities(
                      alertData,
                      availabilityAlert,
                      value2
                    );
                  }
                  this.hasAlerts = this.alertCount > 0;
                });
            }
            break;
          default:
            break;
        }
      });
    });
    this.hasAlerts = this.alertCount > 0;
    this.alertData = alertData;
  }

  updateDayProblemAvailabilities(
    alertData: AlertData,
    issue: SolverIssue,
    resourceAvailability: ResourceAvailability
  ) {
    console.log("updating day problems: ", alertData, issue);
    let date = moment(resourceAvailability.start)
      .startOf("day")
      .parseZone()
      .toDate();
    if (
      alertData.dates.filter((dayData) =>
        moment(dayData.date).isSame(moment(date), "day")
      ).length === 0
    ) {
      let dayProblem = new DayProblems();
      dayProblem.date = date;
      dayProblem.resourceAvailabilities = [];
      dayProblem.tickets = [];
      dayProblem.totalAlerts = 0;
      alertData.dates.push(dayProblem);
      // Availabilities can be loaded asynchronously, so new buckets may be
      // pushed from promise callbacks after the initial pass. Keep the buckets
      // in date order on every insert rather than sorting once up front (#465).
      alertData.dates.sort((a, b) => a.date.getTime() - b.date.getTime());
    }
    let savedDayProblem = alertData.dates.filter((dayData) =>
      moment(dayData.date).isSame(moment(date), "day")
    )[0];

    if (
      savedDayProblem.resourceAvailabilities.filter(
        (value) => value.availabilityId === resourceAvailability.id
      ).length === 0
    ) {
      let availabilityIssue = new AvailabilityIssue();
      availabilityIssue.availability = resourceAvailability;
      availabilityIssue.availabilityId = resourceAvailability.id;
      availabilityIssue.issues = [];

      savedDayProblem.resourceAvailabilities.push(availabilityIssue);
    }

    let savedAvailabilityIssue = savedDayProblem.resourceAvailabilities.filter(
      (value) => value.availabilityId === resourceAvailability.id
    )[0];
    savedAvailabilityIssue.issues.push(translateSolverRule(issue.rule));

    switch (issue.rule) {
      case "Normal Priority Reservation Exceeded":
        savedAvailabilityIssue.normalOver = true;
        break;
      case "High Priority Reservation Exceeded":
        savedAvailabilityIssue.highOver = true;
        break;
      case "Emergency Priority Reservation Exceeded":
        savedAvailabilityIssue.emergencyOver = true;
        break;
      default:
        break;
    }

    savedDayProblem.totalAlerts++;
  }

  ngOnInit() {
    const init = async () => {
      if (this.clientConfigService.config.restrictAlertsByPlannerTeams) {
        const planner = await this.userService.getPlanner();
        if (planner.teamList) {
          this.plannerTeamNames = new Set(planner.teamList.map((t) => t.name));
        }
      }
      const priorityList = await this.priorityStore.loadAll(
        { pageParams: { page: 0, size: 500 } },
        true
      );
      priorityList.resources.forEach((value) => {
        this.priorityMap.set(value.id, value);
      });
      this.solver.status().then((value) => {
        this.updateCounts(value);
        this.lastSolverState = value;
      });
      this.solver.updateStream.subscribe((value) => {
        this.updateCounts(value);
        this.lastSolverState = value;
      });
    };
    init();
  }

  openTicketDialog(ticketId) {
    this.dialog.open(TicketActionDialogComponent, {
      hasBackdrop: true,
      width: "500px",
      panelClass: "toolbar-popup-dialog",
      data: {
        ticketId: ticketId
      }
    });
  }

  openResourceAvailabilityDialog(availabilityId: number) {
    this.dialog.open(AvailabilityEditorComponent, {
      panelClass: "toolbar-popup-dialog",
      data: {
        resourceAvailabilityId: availabilityId
      }
    });
  }
}
